/**
 * ActivityCard Component
 *
 * Displays recent activity in a clean, human-readable format.
 * Transforms raw audit data into activity descriptions.
 *
 * Architecture: Configuration-driven, declarative, functional.
 * - Field configs define how each field is displayed
 * - Ignore rules prevent duplicate entries (e.g., skip ID when Name is present)
 * - Pure transformation pipeline: audits -> activities -> rendered items
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import { decodeTime } from 'ulid';
import { Link as RouterLink } from 'react-router-dom';
import { AppRoutes } from '@consts/routes';
import dayjs from 'dayjs';
import { colors, typography as typographyTokens, radii } from '../../tokens';
import { InfoCard } from './InfoCard';
import { CUT_FEE_MAP, WARRANTY_PLAN_MAP } from '@consts/company';
import type { BillingType, WarrantyType } from '@consts/company';

// =============================================================================
// TYPES
// =============================================================================

export interface AuditRecord {
    email?: string;
    op: 'CREATE' | 'UPDATE' | 'DELETE' | string;
    ts?: string;
    delta?: Record<string, { before?: unknown; after?: unknown }>;
    /** Full before state (contains all fields, not just changed ones) */
    before?: Record<string, unknown>;
    /** Full after state (contains all fields, not just changed ones) */
    after?: Record<string, unknown>;
}

export interface ActivityCardProps {
    audits: AuditRecord[];
    entityPk: string;
    entityType?: 'system' | 'company' | 'reseller' | 'location';
    maxItems?: number;
    sx?: SxProps<Theme>;
}

interface Activity {
    icon: React.ReactNode;
    color: string;
    title: string;
    subtitle: string;
    timestamp: Date;
    user?: string;
    link?: string;
    fromLocation?: { label: string; link: string };
    toLocation?: { label: string; link: string };
}

interface FieldDelta {
    before?: unknown;
    after?: unknown;
}

type Delta = Record<string, FieldDelta>;

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Fields to ignore when their "name" counterpart exists.
 * When both LocationID and LocationName are present, skip LocationID.
 */
const IGNORE_WHEN_NAME_EXISTS: Record<string, string> = {
    LocationID: 'LocationName',
    CompanyID: 'CompanyName',
};

/** Value formatters for specific fields */
const VALUE_FORMATTERS: Record<string, (v: unknown) => string> = {
    CutPlanID: (v) => CUT_FEE_MAP[v as BillingType] || `Plan ${v}`,
    WarrantyPlanID: (v) => WARRANTY_PLAN_MAP[v as WarrantyType] || `Plan ${v}`,
    Status: (v) => ({ 1: 'Active', 2: 'Inactive', 3: 'Decommissioned' }[v as number] || String(v)),
    TrackingFlag: (v) => ({ 1: 'Sales Rep', 2: 'Pin Only', 3: 'None' }[v as number] || String(v)),
    Inactive: (v) => (v === 'Yes' || v === true ? 'Archived' : 'Active'),
    ConsentToSell: (v) => (v ? 'Granted' : 'Revoked'),
    SalesRepTrackingEnabled: (v) => (v === null ? 'Inherit' : v ? 'Enabled' : 'Disabled'),
};

const formatValue = (field: string, value: unknown): string => {
    if (value === null || value === undefined) return '';
    return VALUE_FORMATTERS[field]?.(value) ?? String(value);
};

/** Field display configuration */
interface FieldConfig {
    icon: React.ReactNode;
    color: string;
    format: (before: unknown, after: unknown, delta: Delta) => { title: string; subtitle: string };
    linkBuilder?: (before: unknown, after: unknown) => string | undefined;
}

const defaultIcon = <SettingsOutlinedIcon sx={{ fontSize: 16 }} />;

const FIELD_CONFIGS: Record<string, FieldConfig> = {
    // Location changes - LocationName preferred when available, LocationID as fallback
    LocationName: {
        icon: <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (before, after) => {
            if (!before && after) return { title: 'Assigned to', subtitle: String(after) };
            if (before && !after) return { title: 'Unassigned from', subtitle: String(before) };
            return { title: 'Moved', subtitle: '' }; // Move handled specially in processLocationMove
        },
    },

    LocationID: {
        icon: <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (before, after) => {
            if (!before && after) return { title: 'Assigned to', subtitle: `Location #${after}` };
            if (before && !after) return { title: 'Unassigned from', subtitle: `Location #${before}` };
            return { title: 'Moved', subtitle: '' }; // Move handled specially in processLocationMove
        },
    },

    // Company changes
    CompanyName: {
        icon: <BusinessOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (before, after) => {
            if (!before && after) return { title: 'Assigned to', subtitle: String(after) };
            return { title: 'Renamed to', subtitle: String(after) };
        },
    },

    CompanyID: {
        icon: <BusinessOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (before, after) => {
            if (!before && after) return { title: 'Assigned to', subtitle: `Company #${after}` };
            return { title: 'Company changed to', subtitle: `#${after}` };
        },
        linkBuilder: (_, after) => (after ? `${AppRoutes.Organizations}?type=company&id=${after}` : undefined),
    },

    ResellerID: {
        icon: <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (_, after) => ({ title: 'Reseller changed to', subtitle: after ? `#${after}` : 'None' }),
        linkBuilder: (_, after) => (after ? `${AppRoutes.Organizations}?type=reseller&id=${after}` : undefined),
    },

    ResellerCompanyID: {
        icon: <BusinessOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (_, after) => ({ title: 'Linked to', subtitle: `Company #${after}` }),
    },

    // Billing
    CutPlan: {
        icon: <PaymentOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.warning.main,
        format: (before, after) => {
            if (before && after) return { title: 'Billing plan changed to', subtitle: String(after) };
            if (after) return { title: 'Billing plan set to', subtitle: String(after) };
            return { title: 'Billing plan', subtitle: 'updated' };
        },
    },

    CutPlanID: {
        icon: <PaymentOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.warning.main,
        format: (before, after) => ({
            title: before ? 'Billing plan changed to' : 'Billing plan set to',
            subtitle: formatValue('CutPlanID', after),
        }),
    },

    WarrantyPlan: {
        icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.warning.main,
        format: (before, after) => {
            if (before && after) return { title: 'Warranty plan changed', subtitle: `${before} → ${after}` };
            if (after) return { title: 'Warranty plan set to', subtitle: String(after) };
            return { title: 'Warranty plan', subtitle: 'updated' };
        },
    },

    WarrantyPlanID: {
        icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.warning.main,
        format: (before, after) => ({
            title: before ? 'Warranty plan changed to' : 'Warranty plan set to',
            subtitle: formatValue('WarrantyPlanID', after),
        }),
    },

    // Status
    Status: {
        icon: <SwapHorizIcon sx={{ fontSize: 16 }} />,
        color: colors.info.main,
        format: (_, after) => ({ title: 'Status changed to', subtitle: formatValue('Status', after) }),
    },

    Inactive: {
        icon: <SwapHorizIcon sx={{ fontSize: 16 }} />,
        color: colors.info.main,
        format: (_, after) => ({
            title: 'Status changed to',
            subtitle: after === 'Yes' || after === true ? 'Archived' : 'Active',
        }),
    },

    // Settings
    ConsentToSell: {
        icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.success.main,
        format: (_, after) => ({ title: 'Consent to Sell', subtitle: after ? 'granted' : 'revoked' }),
    },

    SalesRepTrackingEnabled: {
        icon: <PersonOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (_, after) => ({
            title: 'Sales Rep Tracking',
            subtitle: after === true ? 'enabled' : 'disabled',
        }),
    },

    TrackingFlag: {
        icon: <PersonOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (_, after) => ({ title: 'Tracking type changed to', subtitle: formatValue('TrackingFlag', after) }),
    },

    TrackingType: {
        icon: <PersonOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        format: (_, after) => ({ title: 'Tracking type changed to', subtitle: formatValue('TrackingFlag', after) }),
    },

    Country: {
        icon: <PublicOutlinedIcon sx={{ fontSize: 16 }} />,
        color: '#0D9488',
        format: (_, after) => ({ title: 'Country changed to', subtitle: String(after) }),
    },

    PIN: {
        icon: <LockOutlinedIcon sx={{ fontSize: 16 }} />,
        color: '#7C3AED',
        format: () => ({ title: 'PIN', subtitle: 'updated' }),
    },

    Notes: {
        icon: <NotesOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.neutral[500],
        format: () => ({ title: 'Notes', subtitle: 'updated' }),
    },

    UISelection: {
        icon: <SettingsOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.neutral[500],
        format: (_, after) => ({ title: 'UI changed to', subtitle: String(after) }),
    },

    UISelectionID: {
        icon: <SettingsOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.neutral[500],
        format: (_, after) => ({ title: 'UI changed to', subtitle: String(after) }),
    },

    CustomerID: {
        icon: <BusinessOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.neutral[600],
        format: (_, after) => ({ title: 'BC Customer', subtitle: after ? 'linked' : 'unlinked' }),
    },
};

// =============================================================================
// PURE HELPER FUNCTIONS
// =============================================================================

const parseTimestamp = (ts?: string): Date => {
    if (!ts) return new Date();
    try {
        const ulidPart = ts.split('ts#')[1];
        return ulidPart ? new Date(decodeTime(ulidPart)) : new Date();
    } catch {
        return new Date();
    }
};

const getEntityLabel = (type?: string): string =>
    ({ system: 'System', company: 'Company', reseller: 'Reseller', location: 'Location' }[type || ''] || 'Entity');

const toReadable = (str: string): string =>
    str
        .replace(/ID$/i, '')
        .replace(/([A-Z])/g, ' $1')
        .trim();

const parseAssignedSystems = (value: unknown): Map<string, string> => {
    const result = new Map<string, string>();
    if (!value || typeof value !== 'string') return result;
    value.split(',').forEach((entry) => {
        const [stationId, serialNum] = entry.split(':');
        if (stationId && serialNum) result.set(stationId, serialNum);
    });
    return result;
};

/**
 * Determine which fields to skip based on ignore rules.
 * Skip ID fields when their Name counterpart exists in the delta.
 * Skip contextual name fields when their ID counterpart does NOT exist (standalone rename).
 */
/**
 * Determine which fields to skip to avoid duplicate entries.
 * When both an ID field and its Name counterpart exist, skip the ID field
 * (prefer the human-readable name).
 */
const getFieldsToSkip = (delta: Delta): Set<string> =>
    Object.entries(IGNORE_WHEN_NAME_EXISTS)
        .filter(([idField, nameField]) => delta[nameField] && delta[idField])
        .reduce((skip, [idField]) => skip.add(idField), new Set<string>());

// =============================================================================
// ACTIVITY TRANSFORMERS
// =============================================================================

const createBaseActivity = (timestamp: Date, user?: string) => ({
    timestamp,
    user,
});

const createCreateActivity = (entityType: string | undefined, timestamp: Date, user?: string): Activity => ({
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
    color: colors.success.main,
    title: getEntityLabel(entityType),
    subtitle: 'activated',
    ...createBaseActivity(timestamp, user),
});

const createDeleteActivity = (entityType: string | undefined, timestamp: Date, user?: string): Activity => ({
    icon: <SwapHorizIcon sx={{ fontSize: 16 }} />,
    color: colors.error.main,
    title: getEntityLabel(entityType),
    subtitle: 'deleted',
    ...createBaseActivity(timestamp, user),
});

/**
 * Handle AssignedSystems field - creates activities for systems added/removed from location
 */
const processAssignedSystems = (fieldDelta: FieldDelta, timestamp: Date, user?: string): Activity[] => {
    const beforeSystems = parseAssignedSystems(fieldDelta.before);
    const afterSystems = parseAssignedSystems(fieldDelta.after);
    const activities: Activity[] = [];

    // Systems removed from location
    beforeSystems.forEach((serialNum, stationId) => {
        if (!afterSystems.has(stationId)) {
            activities.push({
                icon: <MemoryOutlinedIcon sx={{ fontSize: 16 }} />,
                color: colors.warning.main,
                title: 'System moved from location',
                subtitle: serialNum,
                link: `${AppRoutes.Organizations}?type=system&id=${stationId}`,
                ...createBaseActivity(timestamp, user),
            });
        }
    });

    // Systems added to location
    afterSystems.forEach((serialNum, stationId) => {
        if (!beforeSystems.has(stationId)) {
            activities.push({
                icon: <MemoryOutlinedIcon sx={{ fontSize: 16 }} />,
                color: colors.success.main,
                title: 'System moved to location',
                subtitle: serialNum,
                link: `${AppRoutes.Organizations}?type=system&id=${stationId}`,
                ...createBaseActivity(timestamp, user),
            });
        }
    });

    return activities;
};

/**
 * Handle LocationName field - creates move activity with from/to links
 */
/**
 * Handle location changes (assignment, unassignment, moves).
 * Uses full before/after objects to get location names even when LocationName isn't in delta.
 */
const processLocationMove = (
    fieldDelta: FieldDelta,
    delta: Delta,
    timestamp: Date,
    user?: string,
    field: 'LocationName' | 'LocationID' = 'LocationName',
    fullBefore?: Record<string, unknown>,
    fullAfter?: Record<string, unknown>
): Activity | null => {
    const before = fieldDelta.before;
    const after = fieldDelta.after;

    // Determine labels and IDs based on which field we're processing
    const isNameField = field === 'LocationName';
    const locationIdDelta = delta.LocationID;
    const locationNameDelta = delta.LocationName;

    // Get IDs for linking
    const beforeId = isNameField ? locationIdDelta?.before : before;
    const afterId = isNameField ? locationIdDelta?.after : after;

    // Build labels: "Name (#ID)" when both available, otherwise just name or ID
    const formatLabel = (name: unknown, id: unknown): string => {
        if (name && id) return `${name} (#${id})`;
        if (name) return String(name);
        return `Location #${id}`;
    };

    // Get names from delta first, then fall back to full before/after objects
    const beforeName = isNameField ? before : locationNameDelta?.before ?? fullBefore?.LocationName;
    const afterName = isNameField ? after : locationNameDelta?.after ?? fullAfter?.LocationName;

    const beforeLabel = formatLabel(beforeName, beforeId);
    const afterLabel = formatLabel(afterName, afterId);

    const baseProps = {
        icon: <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />,
        color: colors.primary[500],
        ...createBaseActivity(timestamp, user),
    };

    if (!before && after) {
        return {
            ...baseProps,
            title: 'Assigned to',
            subtitle: afterLabel,
            link: afterId ? `${AppRoutes.Organizations}?type=location&id=${afterId}` : undefined,
        };
    }

    if (before && !after) {
        return {
            ...baseProps,
            title: 'Unassigned from',
            subtitle: beforeLabel,
            link: beforeId ? `${AppRoutes.Organizations}?type=location&id=${beforeId}` : undefined,
        };
    }

    if (before && after) {
        return {
            ...baseProps,
            title: 'Moved',
            subtitle: '',
            fromLocation: {
                label: beforeLabel,
                link: beforeId ? `${AppRoutes.Organizations}?type=location&id=${beforeId}` : '#',
            },
            toLocation: {
                label: afterLabel,
                link: afterId ? `${AppRoutes.Organizations}?type=location&id=${afterId}` : '#',
            },
        };
    }

    return null;
};

/**
 * Process a single field change into an activity
 */
const processFieldChange = (
    field: string,
    fieldDelta: FieldDelta,
    delta: Delta,
    timestamp: Date,
    user?: string
): Activity | null => {
    const config = FIELD_CONFIGS[field];

    if (!config) {
        // Fallback for unknown fields
        return {
            icon: defaultIcon,
            color: colors.neutral[500],
            title: toReadable(field),
            subtitle: 'updated',
            ...createBaseActivity(timestamp, user),
        };
    }

    const { title, subtitle } = config.format(fieldDelta.before, fieldDelta.after, delta);
    const link = config.linkBuilder?.(fieldDelta.before, fieldDelta.after);

    return {
        icon: config.icon,
        color: config.color,
        title,
        subtitle,
        link,
        ...createBaseActivity(timestamp, user),
    };
};

/**
 * Transform a single audit record into activities
 */
const auditToActivities = (audit: AuditRecord, entityType?: string): Activity[] => {
    const timestamp = parseTimestamp(audit.ts);
    const user = audit.email;

    // Handle CREATE
    if (audit.op === 'CREATE') {
        return [createCreateActivity(entityType, timestamp, user)];
    }

    // Handle DELETE
    if (audit.op === 'DELETE') {
        return [createDeleteActivity(entityType, timestamp, user)];
    }

    // Handle UPDATE
    const delta = audit.delta || {};
    const fieldsToSkip = getFieldsToSkip(delta);
    const fullBefore = audit.before;
    const fullAfter = audit.after;

    return Object.entries(delta)
        .filter(([field]) => !fieldsToSkip.has(field))
        .flatMap(([field, fieldDelta]): Activity[] => {
            // Special handler: AssignedSystems
            if (field === 'AssignedSystems') {
                return processAssignedSystems(fieldDelta, timestamp, user);
            }

            // Special handler: LocationName or LocationID (move with from/to)
            if (field === 'LocationName' || field === 'LocationID') {
                const activity = processLocationMove(fieldDelta, delta, timestamp, user, field, fullBefore, fullAfter);
                return activity ? [activity] : [];
            }

            // Standard field processing
            const activity = processFieldChange(field, fieldDelta, delta, timestamp, user);
            return activity ? [activity] : [];
        });
};

/**
 * Transform all audits into sorted activities
 */
const transformAudits = (audits: AuditRecord[], entityType?: string): Activity[] =>
    audits
        .flatMap((audit) => auditToActivities(audit, entityType))
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

// =============================================================================
// COMPONENTS
// =============================================================================

const linkStyles = {
    fontWeight: typographyTokens.fontWeight.semibold,
    color: colors.primary[500],
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
};

const ActivityItem: React.FC<{ activity: Activity }> = ({ activity }) => (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', pb: 2 }}>
        {/* Icon */}
        <Box
            sx={{
                width: 28,
                height: 28,
                borderRadius: radii.sm,
                backgroundColor: `${activity.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: activity.color,
                flexShrink: 0,
            }}>
            {activity.icon}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
                variant='body2'
                sx={{ fontWeight: typographyTokens.fontWeight.normal, color: colors.neutral[700], lineHeight: 1.4 }}>
                {activity.title}

                {/* Move activity: "from X to Y" with both links */}
                {activity.fromLocation && activity.toLocation && (
                    <>
                        {' from '}
                        <Typography component={RouterLink} to={activity.fromLocation.link} sx={linkStyles}>
                            {activity.fromLocation.label}
                        </Typography>
                        {' to '}
                        <Typography component={RouterLink} to={activity.toLocation.link} sx={linkStyles}>
                            {activity.toLocation.label}
                        </Typography>
                    </>
                )}

                {/* Standard linked subtitle */}
                {activity.subtitle && activity.link && !activity.fromLocation && (
                    <Typography component={RouterLink} to={activity.link} sx={linkStyles}>
                        {' '}
                        {activity.subtitle}
                    </Typography>
                )}

                {/* Standard unlinked subtitle */}
                {activity.subtitle && !activity.link && !activity.fromLocation && (
                    <Typography
                        component='span'
                        sx={{ fontWeight: typographyTokens.fontWeight.semibold, color: colors.neutral[900] }}>
                        {' '}
                        {activity.subtitle}
                    </Typography>
                )}
            </Typography>

            <Typography variant='caption' sx={{ color: colors.neutral[500], display: 'block', mt: 0.25 }}>
                {dayjs(activity.timestamp).format('MMMM D, YYYY [at] h:mm A')} - {activity.user}
            </Typography>
        </Box>
    </Box>
);

export const ActivityCard: React.FC<ActivityCardProps> = ({ audits, entityPk, entityType, maxItems = 4, sx }) => {
    const derivedEntityType =
        entityType ??
        (() => {
            const prefix = entityPk.split('#')[0]?.toLowerCase();
            return prefix === 'station' ? 'system' : (prefix as 'system' | 'company' | 'reseller' | 'location');
        })();

    const activities = transformAudits(audits, derivedEntityType).slice(0, maxItems);

    return (
        <InfoCard title='Recent Activity' icon={<ShowChartIcon />} sx={sx}>
            {activities.length > 0 ? (
                <Box>
                    {activities.map((activity, index) => (
                        <ActivityItem key={index} activity={activity} />
                    ))}
                </Box>
            ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant='body2' sx={{ color: colors.neutral[500] }}>
                        No recent activity
                    </Typography>
                </Box>
            )}
        </InfoCard>
    );
};

export default ActivityCard;
